import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { Button, Stack, TextField, Typography } from '@mui/material'
import { EditorField, SchemaEditorAction, SchemaEditorState } from './schemaEditorReducer'
import SortableFieldItem from './SortableFieldItem'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { isDuplicateKey } from './NewSchemaManager'

interface P {
    field: EditorField
    state: SchemaEditorState
    onUpdateField: (fieldId: string, updates: Partial<EditorField>) => void
    dispatch: React.Dispatch<SchemaEditorAction>

}

const ObjectFieldConfig = ({ field, state, onUpdateField, dispatch }: P) => {
    const sensors = useSensors(useSensor(PointerSensor))

    return (
        <Stack spacing={2} sx={{ pl: 6 }}>
            <Typography variant='body2' color='text.secondary'>
                List Fields
            </Typography>
            {/* DESCRIPTION */}
            <TextField
                disabled={field.isDefault}
                label='Field description'
                size='small'
                multiline
                minRows={2}
                value={field.description ?? ''}
                onChange={e =>
                    onUpdateField(field._id, { description: e.target.value })}
                fullWidth
            />

            {/* CHILD FIELDS */}
            <Stack
                spacing={1}
                sx={{
                    pl: 6,
                    borderLeft: '2px solid',
                    borderColor: 'divider'
                }}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={event => {
                        const { active, over } = event
                        if (!over || active.id === over.id) return

                        const oldIndex = field.objectFields.findIndex(
                            f => f._id === active.id
                        )

                        const newIndex = field.objectFields.findIndex(
                            f => f._id === over.id
                        )

                        const reordered = [...field.objectFields]
                        const [moved] = reordered.splice(oldIndex, 1)
                        reordered.splice(newIndex, 0, moved)

                        onUpdateField(field._id, {
                            objectFields: reordered
                        })
                    }}>
                    <SortableContext
                        items={field.objectFields?.map(f => f._id) ?? []}
                        strategy={verticalListSortingStrategy}>
                        {field.objectFields?.map(child => (
                            <SortableFieldItem
                                hasKeyError={isDuplicateKey(field.objectFields, child.key, child._id)}
                                state={state}
                                dispatch={dispatch}
                                key={child._id}
                                field={child}

                                onDelete={id =>
                                    onUpdateField(field._id, {
                                        objectFields: field.objectFields?.filter(
                                            f => f._id !== id
                                        )
                                    })}

                                onUpdateField={(id, updates) => {
                                    onUpdateField(field._id, {
                                        objectFields: field.objectFields?.map(f =>
                                            f._id === id ? { ...f, ...updates } : f
                                        )
                                    })
                                }}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </Stack>

            {/* ADD CHILD */}
            <Stack justifyContent='center' alignItems='flex-end'>
                <Button
                    disabled={field.isDefault}
                    onClick={() => dispatch({
                        type: 'ADD_OBJECT_FIELD',
                        fieldId: field._id })}
                    variant='outlined'
                    size='small'>
                    + Add Child
                </Button>
            </Stack>

        </Stack>
    )
}

export default ObjectFieldConfig
